import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Signal, ArrowRight } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/my-leads", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
    else navigate(res.user.role === "admin" ? "/admin" : "/my-leads", { replace: true });
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5 bg-white font-body">
      {/* Left panel */}
      <div className="col-span-1 lg:col-span-2 flex flex-col justify-between border-r border-zinc-200 bg-zinc-950 p-10 text-white">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-emerald-500 text-black">
            <Signal className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="font-heading text-[15px] font-bold tracking-tight">
            CRM<span className="text-emerald-400">rogveda</span> / CRM
          </div>
        </div>

        <div className="max-w-md">
          <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-emerald-400">Control room</div>
          <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
            Turn WhatsApp<br/>ad chats into<br/>closed sales.
          </h1>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-zinc-400">
            Route Meta leads to your telecallers, track every call, and push quality
            signals back to Meta so your ad spend gets smarter.
          </p>
        </div>

        <div className="text-[11px] uppercase tracking-widest text-zinc-500">
          v1 · Internal build
        </div>
      </div>

      {/* Right panel: form */}
      <div className="col-span-1 lg:col-span-3 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="mb-8">
            <div className="mb-1 text-[11px] uppercase tracking-widest text-zinc-400">Sign in</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-black">Welcome back</h2>
            <p className="mt-1 text-sm text-zinc-500">Use your admin or telecaller account.</p>
          </div>

          <label className="mb-1 block text-xs font-medium text-zinc-600">Email</label>
          <input
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mb-4 w-full rounded-sm border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black focus:ring-1 focus:ring-black"
          />

          <label className="mb-1 block text-xs font-medium text-zinc-600">Password</label>
          <input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mb-4 w-full rounded-sm border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black focus:ring-1 focus:ring-black"
          />

          {error && (
            <div data-testid="login-error" className="mb-4 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-2 rounded-sm bg-black px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-zinc-800 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </form>
      </div>
    </div>
  );
}
